import axios from 'axios';

// Production API
const MFU_USER_API_BASE = 'https://mfuwellnesscenter-server.dietz.asia/api/mfu-user';

// Local Development API

const postWithFallback = async (paths, body) => {
  let lastError = null;

  for (const path of paths) {
    try {
      const response = await axios.post(`${MFU_USER_API_BASE}${path}`, body, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      lastError = error;

      const status = error?.response?.status;
      if (status !== 404) {
        throw error;
      }
    }
  }

  throw lastError;
};

/**
 * Get user's NID from localStorage
 * @returns {string|null} User's NID (National ID)
 */
const getUserNID = () => {
  try {
    // ลองดึงจาก username ก่อน (เก็บ NID จริง)
    const username = localStorage.getItem('username');
    if (username) {
      return username;
    }

    // ถ้าไม่มี username ให้ลองดึงจาก userData
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const data = JSON.parse(userData);
        return data?.id || null;
      } catch {
        // Parse ไม่ได้
      }
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Get health recommendations for user
 * @param {string} nid - National ID (optional, will use from localStorage if not provided)
 * @returns {Promise} Response with recommendations
 */
export const getRecommendations = async (nid = null, visitDate = null) => {
  try {
    const userNID = nid || getUserNID();
    if (!userNID) {
      throw new Error('NID not found. Please login again.');
    }


    const body = { nid: userNID };
    if (visitDate) body.visit_date = visitDate;

    const response = await axios.post(`${MFU_USER_API_BASE}/recommendations`, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get notification history for user
 * @param {string} nid - National ID (optional, will use from localStorage if not provided)
 * @returns {Promise} Response with notifications
 */
export const getNotifications = async (nid = null) => {
  try {
    const userNID = nid || getUserNID();
    if (!userNID) {
      throw new Error('NID not found. Please login again.');
    }


    const response = await axios.post(`${MFU_USER_API_BASE}/notifications`, {
      nid: userNID
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get unread notification count (for bell icon badge)
 * @param {string} nid - National ID (optional, will use from localStorage if not provided)
 * @returns {Promise} Response with unread_count
 */
export const getUnreadNotificationCount = async (nid = null) => {
  try {
    const userNID = nid || getUserNID();
    if (!userNID) {
      throw new Error('NID not found. Please login again.');
    }

    return await postWithFallback(
      ['/notifications/unread-count', '/unread-count'],
      { nid: userNID }
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Mark all notifications as read (when user clicks bell icon)
 * @param {string} nid - National ID (optional, will use from localStorage if not provided)
 * @returns {Promise} Response with marked_count
 */
export const markNotificationsAsRead = async (nid = null) => {
  try {
    const userNID = nid || getUserNID();
    if (!userNID) {
      throw new Error('NID not found. Please login again.');
    }

    return await postWithFallback(
      ['/notifications/mark-read', '/mark-read'],
      { nid: userNID }
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Get PDF download URL for health report
 * @param {number} historyId - History ID from notification
 * @returns {string} PDF download URL
 */
export const getPDFDownloadUrl = (historyId) => {
  return `${MFU_USER_API_BASE}/download-pdf/${historyId}`;
};

/**
 * Download PDF health report
 * @param {number} historyId - History ID from notification
 * @returns {Promise} Response with PDF content
 */
export const downloadPDF = async (historyId) => {
  try {

    const url = getPDFDownloadUrl(historyId);

    // Open PDF in new window
    window.open(url, '_blank');

    return { success: true };
  } catch (error) {
    throw error;
  }
};

/**
 * Create share token for public health report (MFU User / LINE OA path)
 * Uses history_id to look up visit_date from DB directly — more reliable than checkup_date from notification
 * @param {string} nid - National ID
 * @param {number} historyId - health_recommendations_history.id (notification.id)
 * @param {number} expiresInHours - Token expiration time in hours (default: 24)
 * @returns {Promise} Response with share_url
 */
export const createShareTokenMFUUser = async (nid, historyId = null, expiresInHours = 24, visitDate = null) => {
  try {

    const body = { nid, expires_in_hours: expiresInHours };
    if (historyId) body.history_id = historyId;
    if (visitDate && !historyId) body.visit_date = visitDate;

    const response = await axios.post(`${MFU_USER_API_BASE}/create-share-token`, body, {
      headers: { 'Content-Type': 'application/json' }
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create share token for public health report (HR path — kept for reference, not used by LINE OA)
 * @param {string} nid - National ID
 * @param {number} expiresInHours - Token expiration time in hours (default: 24)
 * @returns {Promise} Response with share_url
 */
export const createShareToken = async (nid, expiresInHours = 24, visitDate = null) => {
  try {

    const body = { nid, expires_in_hours: expiresInHours };
    if (visitDate) body.visit_date = visitDate;

    const response = await axios.post('https://mfuwellnesscenter-server.dietz.asia/api/admin-hr/create-share-token', body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get health record detail from notification
 * This extracts the full health record data from notification data
 * @param {object} notification - Notification object from getNotifications()
 * @returns {object} Health record data formatted for HealthReportModal
 */
export const getHealthRecordFromNotification = (notification) => {
  if (!notification) {
    throw new Error('Notification data is required');
  }

  // แปลงข้อมูลจาก notification ให้เป็นรูปแบบที่ modal ต้องการ
  const record = {
    id: notification.id,
    hn: notification.hn || '-',
    nid: notification.nid || '-',
    firstname: notification.firstname || '-',
    lastname: notification.lastname || '-',
    gender: notification.gender || '-',
    age: notification.age || 0,
    checkupDate: notification.checkup_date || notification.sent_at || '-',

    // ข้อมูลสุขภาพพื้นฐาน
    height: notification.height || '-',
    weight: notification.weight || '-',
    bmi: notification.bmi || '-',
    waistline: notification.waistline || '-',
    systolic_bp: notification.systolic_bp || '-',
    diastolic_bp: notification.diastolic_bp || '-',
    pulse: notification.pulse || '-',

    // คำแนะนำจากแพทย์
    recommendations: notification.recommendations || {
      doctor_advice: notification.doctor_advice || '-',
      nutrition_advice: notification.nutrition_advice || '-',
      exercise_advice: notification.exercise_advice || '-',
      sleep_advice: notification.sleep_advice || '-'
    },

    // ข้อมูลการส่ง
    sent_by: notification.sent_by || '-',
    sent_at: notification.sent_at || '-',

    // Parameters (ถ้ามี)
    parameters: notification.parameters || [],

    // Status
    approvalStatus: 'approved' // ถ้าส่งมาแล้วถือว่า approved
  };

  return record;
};
